import { createTrackingConfig, getLabels } from '../../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../../config/appConfig';

const trainingTrackingLabels = getLabels('Training');
const trainingTrackingBase = createTrackingConfig('Training');

export const trainingConfig = {
  entityName: 'Training',
  
  // Entity information for file attachments
  entityTypeId: null, // Training doesn't use file attachments
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/training',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      bulkDelete: '/bulk-delete',
      nextCode: '/next-code'
    },
    lookupEndpoints: {
      trainingTypes: '/training/training-types'
    },
    // SWR invalidation endpoints - for refreshing data after operations
    swrInvalidation: {
      mainList: '/training'
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '30%',
    defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions,
    defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle,
    tableVariant: 'default',
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth
  },

  // Features configuration
  features: {
    fileAttachments: false, // Disable file attachment functionality for trainings
    masterEntityTypeId: null, // Training doesn't have entity type for audit logs
  },
  
  // Custom handlers
  customHandlers: {
    onSave: null,
    onDelete: null,
    onLoad: null,
    onFormOpen: null
  },
  
  // Service configuration
  service: {
    name: 'trainingService',
    methods: {
      getAll: 'getAllTrainings',
      getById: 'getTrainingById', 
      create: 'createTraining',
      update: 'updateTraining',
      delete: 'deleteTraining',
      validate: 'validateData'
    }
  },
  
  // Display Configuration
  displayName: 'Training Management',
  description: 'Training management system for creating and managing training courses',
  icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  
  // Fields to remove from forms (relations, computed, system fields)
  fieldsToRemove: [
    'id', 'trainingType', 'createdBy', 'updatedBy',
    'createdById', 'updatedById',
    'createdAt', 'updatedAt', 'trainingAssignments',
  ],

  // UNIFIED FIELD CONFIGURATION - Single source of truth
  fields: [
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    {
      name: 'code',
      label: 'Training Code',
      type: 'text',
      required: true,
      placeholder: 'Enter training code (or leave blank to auto-generate)',
      table: { header: 'Code', width: '120px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 50,
        message: 'Training code is required and must not exceed 50 characters'
      }
    },
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Enter training title',
      table: { header: 'Title', width: '250px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 255,
        message: 'Title is required and must not exceed 255 characters'
      }
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: false,
      placeholder: 'Enter training description',
      rows: 4,
      table: { header: 'Description', width: '300px', sortable: false },
      filterable: false,
      validation: {
        required: false,
        maxLength: 1000,
        message: 'Description must not exceed 1000 characters'
      }
    },
    {
      name: 'trainingTypeId',
      label: 'Training Type',
      type: 'select',
      required: false,
      lookupEndpoint: 'trainingTypes',
      options: [],
      table: { 
        header: 'Type', 
        width: '150px', 
        sortable: true,
        displayField: 'trainingType'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid training type'
      }
    },
    {
      name: 'duration',
      label: 'Duration',
      type: 'number',
      required: false,
      placeholder: 'Enter duration',
      table: { header: 'Duration', width: '100px', sortable: true },
      filterable: false,
      validation: {
        required: false,
        min: 0,
        message: 'Duration must be a positive number'
      }
    },
    {
      name: 'durationUnit',
      label: 'Duration Unit',
      type: 'select',
      required: false,
      options: [
        { value: 'minutes', label: 'Minutes' },
        { value: 'hours', label: 'Hours' },
        { value: 'days', label: 'Days' }
      ],
      table: { header: 'Unit', width: '80px', sortable: true },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: false
      }
    },
    {
      name: 'content',
      label: 'Content',
      type: 'textarea',
      required: false,
      placeholder: 'Enter training content/description',
      rows: 6,
      table: false,
      filterable: false,
      validation: {
        required: false,
        maxLength: 5000,
        message: 'Content must not exceed 5000 characters'
      }
    },
    {
      name: 'isRequired',
      label: 'Required',
      type: 'switch',
      required: false,
      defaultValue: false,
      switchLabel: 'Mandatory training',
      labelPosition: 'left',
      table: { 
        header: 'Required', 
        width: '80px', 
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      filterType: 'boolean',
      validation: {
        required: false
      }
    },
    {
      name: 'recurrenceDays',
      label: 'Recurrence (Days)',
      type: 'number',
      required: false,
      placeholder: 'Enter recurrence in days',
      table: { header: 'Recurrence', width: '100px', sortable: true },
      filterable: false,
      validation: {
        required: false,
        min: 0,
        message: 'Recurrence days must be a positive number'
      }
    },
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      required: false,
      defaultValue: true,
      switchLabel: 'Training is active',
      labelPosition: 'left',
      table: { 
        header: 'Active', 
        width: '80px', 
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      filterType: 'boolean',
      validation: {
        required: false
      }
    },
    {
      name: 'createdAt',
      label: 'Created',
      type: 'date',
      required: false,
      table: { 
        header: 'Created', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange'
    },
    {
      name: 'updatedAt',
      label: 'Updated',
      type: 'date',
      required: false,
      table: false,
      filterable: false
    }
  ],
  
  // COMPUTED CONFIGURATIONS - Generated from unified fields (no maintenance needed!)
  get tableColumns() {
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        originalField: field.name
      }));
    
    return columns;
  },

  exportFileName: 'qms_trainings',
  
  // Backend field mappings
  fieldMappings: {
    trainingType: 'trainingType.name',
    createdBy: 'createdBy.userDisplayName',
    updatedBy: 'updatedBy.userDisplayName'
  },
  
  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  // Custom Sort Orders
  customSortOrders: {
    isActive: ['true', 'false'], // Active trainings first
    isRequired: ['true', 'false'], // Required trainings first
    title: 'asc' // Alphabetical by title
  },

  // Filter Groups Configuration - Groups filters into tabs for better organization
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['code', 'title', 'trainingTypeId', 'isRequired', 'isActive']
    }
  ],

  // Form Layout Configuration - Single source of truth
  formSections: [
    {
      title: 'Training Information',
      icon: '📚',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['code', 'title', 'description', 'trainingTypeId', 'duration', 'durationUnit', 'isRequired', 'recurrenceDays', 'isActive', 'content'],
      fieldSpans: {
        'code': 'md:col-span-6',
        'title': 'md:col-span-6',
        'description': 'md:col-span-12',
        'trainingTypeId': 'md:col-span-6',
        'duration': 'md:col-span-3',
        'durationUnit': 'md:col-span-3',
        'isRequired': 'md:col-span-6',
        'recurrenceDays': 'md:col-span-6',
        'isActive': 'md:col-span-6',
        'content': 'md:col-span-12'
      }
    }
  ],

  // Tracking — show audit users by display name (nested objects from API / selected row)
  trackingSection: {
    ...trainingTrackingBase,
    fields: trainingTrackingBase.fields.map((f) =>
      f.id === 'created'
        ? {
            ...f,
            userLabel: 'Created user:',
            dateLabel: trainingTrackingLabels.createdDateLabel,
            userEmptyText: trainingTrackingLabels.notSpecifiedText,
            dateEmptyText: trainingTrackingLabels.notSpecifiedText,
          }
        : f.id === 'updated'
          ? {
              ...f,
              userLabel: 'Updated user:',
              dateLabel: trainingTrackingLabels.updatedDateLabel,
              userEmptyText: trainingTrackingLabels.notModifiedText,
              dateEmptyText: trainingTrackingLabels.neverUpdatedText,
            }
          : f,
    ),
  },

  // Labels and Text Configuration (uses predefined Training labels + custom overrides)
  labels: {
    // Using predefined Training labels from centralized labels file
    // You can override any label by uncommenting and modifying:
    singular: 'Training',
    plural: 'Trainings',
    pageTitle: 'Training Management',
    pageSubtitle: 'Create and manage training courses for employee compliance and development',
    searchPlaceholder: 'Search trainings by code, title, or training type...',
    viewTitle: 'View Training',
    noSelectionText: 'Select a training to see details'
  },

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };
    
    // Convert foreign key fields to integers or remove if empty
    const foreignKeyFields = ['trainingTypeId'];
    foreignKeyFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const numValue = parseInt(transformed[field], 10);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    // Convert number fields
    const numberFields = ['duration', 'recurrenceDays'];
    numberFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const numValue = parseFloat(transformed[field]);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });
    
    return transformed;
  },

  // Notification messages
  messages: {
    loading: 'Loading trainings...',
    noData: 'No trainings found. Create your first training to get started.',
    loadError: 'Failed to load trainings',
    saveSuccess: 'Training saved successfully!',
    saveError: 'Failed to save training',
    deleteSuccess: 'Training deleted successfully!',
    deleteError: 'Failed to delete training',
    deleteConfirm: 'Are you sure you want to delete this training? This action cannot be undone.'
  },

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null, // Will be set after validation module loads

  // Help text for field descriptions
  helpText: {
    code: { short: 'Unique training code (auto-generated if not provided).' },
    title: { short: 'Training title or name for identification.' },
    description: { short: 'Brief description of what the training covers and its objectives.' },
    trainingTypeId: { short: 'Type or category of training (e.g., Safety, Quality, Compliance).' },
    duration: { short: 'Duration of the training course or session.' },
    durationUnit: { short: 'Unit for duration measurement (minutes, hours, or days).' },
    content: { short: 'Detailed training content, curriculum, or learning materials description.' },
    isRequired: { short: 'Whether this training is mandatory for employees.' },
    recurrenceDays: { short: 'Number of days before this training needs to be repeated or renewed.' },
    isActive: { short: 'Whether the training is currently active and available for assignment.' }
  }
};

export default trainingConfig;

